import firebaseApp from "../firebase/credenciales";
import { getFirestore, collection, getDocs, query, where } from "firebase/firestore";
const db = getFirestore(firebaseApp);

/**
 * Obtiene el reporte de onboarding para un trabajador específico o todos los de la empresa
 * @param {string} ruc RUC de la empresa
 * @param {string|null} dni DNI del trabajador (null para todos)
 * @param {string} mes Mes seleccionado (1-12)
 * @param {string} ano Año seleccionado
 * @param {Object} userData Datos del trabajador actual (si es consulta individual)
 * @param {boolean} soloMedico Filtrar solo documentos médicos
 * @param {string|null} nombreDocumento Filtrar por un nombre de documento específico (PARA REPORTE MASIVO)
 */
export default async function getReporteOnboarding(ruc, dni, mes, ano, userData = null, soloMedico = false, nombreDocumento = null) {
    const mesNum = parseInt(mes);
    const anoNum = parseInt(ano);
    
    let allRecords = [];

    // Si no hay DNI, primero debemos obtener todos los usuarios de la empresa
    let usuariosParaConsultar = [];
    if (!dni) {
        // Limpiar el RUC de espacios por si acaso
        const rucLimpio = (ruc || "20538856674").toString().trim();
        const usersRef = collection(db, "users");
        
        // Intentar búsqueda por rucEmpresa
        let q = query(usersRef, where("rucEmpresa", "==", rucLimpio));
        let snap = await getDocs(q);
        
        // Si no encuentra nada por rucEmpresa, intentar por ruc (algunos modelos lo usan así)
        if (snap.empty) {
            const q2 = query(usersRef, where("ruc", "==", rucLimpio));
            snap = await getDocs(q2);
        }
        
        if (snap.empty) {
             console.warn("No se encontraron usuarios con el RUC:", rucLimpio);
        }

        snap.forEach(doc => {
            const data = doc.data();
            // Filtro manual de activo para ser más flexible
            if (data.activo === true || data.activo === "true" || data.activo === undefined) {
                usuariosParaConsultar.push({
                    dni: data.dni,
                    colaborador: `${data.apellidos || ""} ${data.name || ""}`.trim(),
                    email: data.email || ""
                });
            }
        });
    } else {
        usuariosParaConsultar = [{
            dni: dni,
            colaborador: userData ? `${userData.apellidos || ""} ${userData.name || ""}`.trim() : "Trabajador",
            email: userData?.email || ""
        }];
    }

    // Iterar por cada usuario en paralelo para obtener su legajo
    const promesas = usuariosParaConsultar.map(async (u) => {
        const registrosUsuario = [];
        try {
            const legajoRef = collection(db, `onboarding/${u.dni}/legajo`);
            const legajoSnap = await getDocs(legajoRef);

            legajoSnap.forEach(doc => {
                const data = doc.data();
                // Parsing de fecha más robusto
                let date;
                const rawFecha = data.fechaDoc || data.fechaEnvio || data.fechaFirma || data.fechaVista || data.fecha;
                
                if (!rawFecha) return; // Si no hay ninguna fecha, saltar

                if (typeof rawFecha.toDate === "function") {
                    date = rawFecha.toDate();
                } else if (rawFecha.seconds) {
                    date = new Date(rawFecha.seconds * 1000);
                } else if (rawFecha instanceof Date) {
                    date = rawFecha;
                } else {
                    return; // No hay fecha válida, saltar registro
                }

                const m = date.getMonth() + 1;
                const y = date.getFullYear();
                
                // También podemos usar los campos directos si existen (mes: "03", ano: 2026)
                const mDB = data.mes ? parseInt(data.mes) : m;
                const yDB = data.ano ? parseInt(data.ano) : y;

                // Filtrar por mes y año (Priorizando campos directos si existen)
                if (mDB === mesNum && yDB === anoNum) {
                    const tituloOriginal = data.nombre || data.tipo || data.titulo || "Documento";
                    const idDoc = String(data.documentId || data.idDocument || data.id || "");
                    
                    // Si hay un nombre de documento específico (REPORTE MASIVO)
                    if (nombreDocumento && nombreDocumento.trim() !== "") {
                        const nomDocSelect = nombreDocumento.toLowerCase().trim();
                        
                        // IMPORTANTE: Revisar TODOS los campos potenciales de nombre
                        const campos = [
                            data.tipo,
                            data.nombre,
                            data.titulo,
                            data.title,
                            data.documento
                        ].map(v => (v || "").toLowerCase().trim());

                        // Si ninguno coincide exactamente, descartar
                        if (!campos.includes(nomDocSelect)) {
                            return;
                        }
                    }

                    const tituloLower = tituloOriginal.toLowerCase();
                    const esMedico = idDoc === "9" || idDoc === "32" || 
                                     tituloLower.includes("médico") || tituloLower.includes("médica") || 
                                     tituloLower.includes("salud") || tituloLower.includes("emo");

                    // Si estamos en modo SOLO MÉDICO, ignorar el resto
                    if (soloMedico && !esMedico) return;

                    registrosUsuario.push({
                        dni: u.dni,
                        colaborador: u.colaborador, // Para reportes masivos necesitamos el nombre aquí
                        email: u.email,
                        tipo: tituloOriginal,
                        tipoId: idDoc,
                        periodo: mes.padStart(2, '0'),
                        ano: ano.toString(),
                        fechaDoc: data.fechaDoc,
                        ruc: data.ruc || ruc,
                        contador: data.contador || (data.fechaVista ? 1 : 0),
                        plataforma: data.plataforma || "web",
                        fechaFirma: data.fechaFirma || (data.linkFirmado ? data.fechaDoc : null),
                        fechaVista: data.fechaVista || null
                    });
                }
            });
        } catch (error) {
            console.error(`Error al obtener legajo del usuario ${u.dni}:`, error);
        }
        return registrosUsuario;
    });

    const resultados = await Promise.all(promesas);
    allRecords = resultados.flat();

    return allRecords;
}
